import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import {
    Layout,
    TableSkeleton,
    PageHeader,
    StatCard,
    SectionCard,
    FilterBar,
    FilterField,
    EmptyState,
    IconAction,
    StatusTag
} from '../common';
import { Select, DatePicker, Table, Button, Spin, Progress } from 'antd';
import { Globe, FilterX, Store, Percent, ClipboardCheck, LayoutGrid, Image, ShieldAlert, TriangleAlert, RotateCw, Building2, Inbox } from 'lucide-react';
import dayjs from 'dayjs';
import { brand, semantic } from '../../theme/tokens';
import NumericDistributionController, { CHANNEL_OPTIONS, DEFAULT_FILTERS } from './numericDistribution.controller';
import { sosMonthColumns, titleize } from '../share-of-shelf/sosHeat';
import { DnLegend } from '../share-of-shelf/elements/HeatLegend';
import { monthLabel, monthHeader } from '../uso-de-shelf/usageHeat';
import { PresenceCell, PresenceLegend } from './elements/Presence';
import StoreDnDrawer from './StoreDnDrawer';

const { RangePicker } = DatePicker;

/** "Meses: Ene 2026 · Feb 2026" footer under heat tables. */
const MonthsFooter = ({ months }) =>
    months.length ? (
        <div className="border-t border-surface-100 px-5 py-3 text-xs text-surface-400">Meses: {months.map(monthLabel).join(' · ')}</div>
    ) : null;

/** Colored tag dot for the Etiqueta select options. */
const tagOption = (opt) => (
    <span className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: opt.data.color || brand[500] }} />
        {opt.data.label}
    </span>
);

const linkClass = 'text-left font-medium text-primary-600 hover:text-primary-700 hover:underline';

class NumericDistribution extends Inherit(NumericDistributionController, Component) {
    state = {
        loading: true,
        dataLoading: false,
        storesLoading: false,
        accessDenied: false,
        error: false,
        clientOptions: [],
        tagOptions: [],
        storeTypeOptions: [], // Tipo de PDV options (StoreType.value)
        productOptions: [], // Table 2 product filter, scoped to the selected client's checks
        storeOptions: [], // PDV options, scoped to the selected client(s)
        data: null, // { months, stats, rows } — categories, each with products → clients
        clientStores: null, // { months, stores }
        filters: { ...DEFAULT_FILTERS },
        dateRange: [dayjs().subtract(150, 'day'), dayjs()],
        selectedClientId: null,
        selectedClientName: null,
        table2Product: null, // barcode of the product whose presence Table 2 shows (null = cualquier producto)
        drawerOpen: false,
        drawerStore: null
    };

    // ---- Table 1: DN by category (rows × months %). Expand a category → its products; expand a
    // product → the clients that stock it. Three-level drill: categoría → productos → clientes. ----
    get rowColumns() {
        const months = this.state.data?.months || [];
        return [
            {
                title: 'Categoría',
                dataIndex: 'label',
                key: 'label',
                fixed: 'left',
                width: 280,
                render: (t, r) => (
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-surface-900">{titleize(t)}</span>
                        <StatusTag className="!h-5 !px-1.5 tabular-nums">{r.productCount} productos</StatusTag>
                    </div>
                )
            },
            ...sosMonthColumns(months, 'dn')
        ];
    }

    // Level 2: the products within a category. Each product expands to its clients.
    renderCategoryProducts(category) {
        const months = this.state.data?.months || [];
        const columns = [
            {
                title: 'Producto',
                dataIndex: 'label',
                key: 'label',
                fixed: 'left',
                width: 260,
                render: (t, r) => (
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-surface-900">{t}</span>
                            <StatusTag className="!h-5 !px-1.5 tabular-nums">{r.clientCount} clientes</StatusTag>
                        </div>
                        {r.sub ? <span className="text-xs text-surface-400">{titleize(r.sub)}</span> : null}
                    </div>
                )
            },
            ...sosMonthColumns(months, 'dn')
        ];
        return (
            <Table
                className="heat-table"
                columns={columns}
                dataSource={category.products || []}
                rowKey={(r) => r.key}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                expandable={{ expandedRowRender: (product) => this.renderProductClients(product), rowExpandable: (p) => (p.clients || []).length > 0 }}
            />
        );
    }

    // Level 3: the clients stocking a product. Clicking a client loads its PDV in Table 2.
    renderProductClients(product) {
        const months = this.state.data?.months || [];
        const columns = [
            {
                title: 'Cliente',
                dataIndex: 'clientName',
                key: 'clientName',
                fixed: 'left',
                width: 240,
                render: (t, r) => (
                    <button
                        type="button"
                        className={`inline-flex items-center gap-1.5 ${linkClass}`}
                        title="Ver PDV de este cliente"
                        onClick={() => this.selectClient(r.clientId, r.clientName)}>
                        <Building2 className="text-surface-400" />
                        {t}
                    </button>
                )
            },
            ...sosMonthColumns(months, 'dn')
        ];
        return (
            <Table
                className="heat-table"
                columns={columns}
                dataSource={product.clients || []}
                rowKey={(r) => r.clientId || r.clientName}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
            />
        );
    }

    get storeColumns() {
        const months = this.state.clientStores?.months || [];
        const monthCols = months.map((m) => ({
            title: monthHeader(m),
            key: m,
            align: 'center',
            width: 84,
            onCell: () => ({ style: { padding: 0 } }),
            render: (_, r) => <PresenceCell audited={!!r.byMonth?.[m]?.audited} present={!!r.byMonth?.[m]?.present} />
        }));
        return [
            {
                title: 'PDV',
                dataIndex: 'storeName',
                key: 'storeName',
                fixed: 'left',
                width: 300,
                render: (t, r) => (
                    <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <button type="button" className={`block ${linkClass}`} onClick={() => this.openStore(r)}>
                                {t}
                            </button>
                            <span className="text-xs text-surface-400">{r.storeType || 'Sin tipo'}</span>
                        </div>
                        <IconAction icon={<Image />} label="Ver imágenes" onClick={() => this.openStore(r)} />
                    </div>
                )
            },
            ...monthCols,
            {
                title: 'Actual',
                key: '__current',
                align: 'center',
                width: 104,
                render: (_, r) =>
                    r.current == null ? (
                        <span className="text-surface-300">—</span>
                    ) : r.current ? (
                        <StatusTag tone="success" dot>Presente</StatusTag>
                    ) : (
                        <StatusTag tone="danger" dot>Ausente</StatusTag>
                    )
            }
        ];
    }

    renderStats() {
        const { data } = this.state;
        const stats = data?.stats || { pdvCreados: 0, pdvAuditados: 0, coverage: 0 };
        const rowCount = data?.rows?.length || 0;
        return (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-4">
                <StatCard label="PDV creados" value={stats.pdvCreados.toLocaleString()} icon={<Store />} tone="brand" />
                <StatCard label="PDV auditados DN" value={stats.pdvAuditados.toLocaleString()} icon={<ClipboardCheck />} tone="violet" />
                <StatCard
                    label="Cobertura DN (auditados / creados)"
                    value={`${(stats.coverage || 0).toFixed(2)}%`}
                    icon={<Percent />}
                    tone="success"
                    footer={
                        <Progress
                            percent={Math.round(stats.coverage || 0)}
                            showInfo={false}
                            strokeColor={semantic.success.solid}
                            size={['100%', 6]}
                            className="!m-0"
                        />
                    }
                />
                <StatCard label="Categorías" value={rowCount.toLocaleString()} icon={<LayoutGrid />} tone="info" />
            </div>
        );
    }

    renderTable2() {
        const { selectedClientId, selectedClientName, clientStores, storesLoading, table2Product, clientOptions, productOptions } = this.state;
        const months = clientStores?.months || [];

        return (
            <SectionCard
                flush
                className="mt-4"
                icon={<Store />}
                title={<>PDV del cliente · Presencia</>}
                description="Presencia por PDV y mes · haz clic en un PDV para ver la DN por producto"
                extra={<PresenceLegend missing={<>— sin auditar</>} />}>
                <div className="flex flex-wrap items-end gap-3 border-b border-surface-100 px-5 py-3">
                    <FilterField label="Cliente" width={260}>
                        <Select
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder="Selecciona un cliente"
                            value={selectedClientId}
                            options={clientOptions}
                            onChange={(v) => this.pickTable2Client(v)}
                        />
                    </FilterField>
                    <FilterField label="Producto" width={300}>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Cualquier producto"
                            disabled={!selectedClientId}
                            notFoundContent={selectedClientId ? 'Sin productos auditados' : 'Selecciona un cliente'}
                            value={table2Product}
                            options={productOptions}
                            onChange={(v) => this.setTable2Product(v)}
                            optionRender={(opt) => (
                                <div>
                                    <div>{opt.data.label}</div>
                                    {opt.data.category ? <span className="text-xs text-surface-400">{titleize(opt.data.category)}</span> : null}
                                </div>
                            )}
                        />
                    </FilterField>
                </div>
                {!selectedClientId ? (
                    <EmptyState
                        icon={<Building2 />}
                        title="Elige un cliente"
                        description="Selecciona un cliente (arriba o desde la tabla anterior) para ver la presencia en sus PDV."
                    />
                ) : storesLoading ? (
                    <div className="flex justify-center py-12">
                        <Spin />
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-2 px-5 py-3 text-xs text-surface-500">
                            <span className="font-medium text-surface-900">{selectedClientName}</span>
                            <span className="text-surface-300">·</span>
                            {table2Product ? `Producto: ${this.table2ProductLabel()}` : 'Cualquier producto disponible'}
                        </div>
                        <Table
                            className="heat-table"
                            columns={this.storeColumns}
                            dataSource={this.storeRows}
                            rowKey={(r) => r.storeId}
                            size="middle"
                            scroll={{ x: 'max-content' }}
                            pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t) => `${t} PDV` }}
                            locale={{ emptyText: <EmptyState icon={<Inbox />} title="Sin datos" description="Sin PDV auditados en el rango" /> }}
                        />
                        <MonthsFooter months={months} />
                    </>
                )}
            </SectionCard>
        );
    }

    renderFilters() {
        const { tagOptions, clientOptions, storeOptions, storeTypeOptions, filters, dateRange } = this.state;

        return (
            <FilterBar
                actions={
                    <Button icon={<FilterX />} onClick={() => this.clearFilters()}>
                        Limpiar
                    </Button>
                }>
                <FilterField label="Canal" width={190}>
                    <Select
                        mode="multiple"
                        allowClear
                        maxTagCount="responsive"
                        placeholder="Todos"
                        value={filters.channel}
                        options={CHANNEL_OPTIONS}
                        onChange={(v) => this.setFilter('channel', v || [])}
                    />
                </FilterField>
                <FilterField label="Cliente" width={230}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todos"
                        value={filters.clientId}
                        options={clientOptions}
                        onChange={(v) => this.setFilter('clientId', v || [])}
                    />
                </FilterField>
                <FilterField label="PDV" width={230}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        disabled={!filters.clientId?.length}
                        placeholder={filters.clientId?.length ? 'Todos' : 'Elige cliente(s) primero'}
                        value={filters.storeId}
                        options={storeOptions}
                        onChange={(v) => this.setFilter('storeId', v || [])}
                    />
                </FilterField>
                <FilterField label="Tipo de PDV" width={200}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todos"
                        value={filters.storeType}
                        options={storeTypeOptions}
                        onChange={(v) => this.setFilter('storeType', v || [])}
                    />
                </FilterField>
                <FilterField label="Etiqueta" width={200}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todas"
                        value={filters.tagId}
                        options={tagOptions}
                        onChange={(v) => this.setFilter('tagId', v || [])}
                        optionRender={tagOption}
                    />
                </FilterField>
                <FilterField label="Rango de fechas" width={250}>
                    <RangePicker value={dateRange} allowClear={false} format="DD/MM/YYYY" onChange={(range) => this.setDateRange(range)} />
                </FilterField>
            </FilterBar>
        );
    }

    render() {
        const { loading, dataLoading, accessDenied, error, data, drawerOpen, drawerStore, selectedClientName } = this.state;
        const months = data?.months || [];

        return (
            <Layout current="numeric-distribution">
                {this.props.contextHolder}

                <PageHeader
                    icon={<Globe />}
                    title="Distribución Numérica"
                    description="La Distribución Numérica (DN) mide el % de PDV auditados en los que está presente cada categoría, por mes. Expande una categoría para ver sus clientes; selecciona un cliente para ver la presencia por PDV."
                />

                {accessDenied ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<ShieldAlert />}
                            title="Acceso restringido"
                            description="Esta vista está disponible para administradores y gerentes."
                        />
                    </div>
                ) : error ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<TriangleAlert />}
                            title="Error al cargar la Distribución Numérica"
                            action={
                                <Button icon={<RotateCw />} onClick={() => this.retry()}>
                                    Reintentar
                                </Button>
                            }
                        />
                    </div>
                ) : loading ? (
                    <TableSkeleton rows={10} columns={8} />
                ) : (
                    <div className="animate-in">
                        {this.renderFilters()}

                        {this.renderStats()}

                        <SectionCard
                            flush
                            icon={<LayoutGrid />}
                            title="Distribución Numérica por Categoría"
                            description="Expande una categoría para ver sus productos, y un producto para ver sus clientes"
                            extra={<DnLegend />}>
                            <Table
                                className="heat-table"
                                columns={this.rowColumns}
                                dataSource={data?.rows || []}
                                loading={dataLoading}
                                rowKey={(r) => r.key}
                                size="middle"
                                scroll={{ x: 'max-content' }}
                                expandable={{ expandedRowRender: (record) => this.renderCategoryProducts(record), rowExpandable: (r) => (r.products || []).length > 0 }}
                                pagination={false}
                                locale={{
                                    emptyText: (
                                        <EmptyState icon={<Inbox />} title="Sin datos" description="Sin datos de distribución en el rango/filtros seleccionados" />
                                    )
                                }}
                            />
                            <MonthsFooter months={months} />
                        </SectionCard>

                        {this.renderTable2()}
                    </div>
                )}

                <StoreDnDrawer
                    open={drawerOpen}
                    onClose={() => this.closeStore()}
                    data={this.data}
                    params={this.dateParams()}
                    storeId={drawerStore?.storeId}
                    storeName={drawerStore?.storeName}
                    clientName={selectedClientName}
                />
            </Layout>
        );
    }
}

export default withNavigate(NumericDistribution);
